import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type { CurrentUser } from '../lib/types';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const r = await api<{ user: CurrentUser }>('/api/auth/me');
        return r.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    // Don't bombard the server with /me probes.
    staleTime: 30_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<{ user: CurrentUser }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['me'], data.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // Use a direct fetch with text() parsing so a 204 No Content body
      // can't trip up the api wrapper's JSON parse. Also swallow errors —
      // the user's INTENT is to log out, and they're going to /login
      // regardless. If the network request failed, the cookie may still
      // be valid server-side, but they wanted out — best-effort.
      try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      } catch {
        // ignore — we still want to clear local state below
      }
    },
    onSettled: () => {
      qc.setQueryData(['me'], null);
      qc.clear();
      window.location.href = '/login';
    },
  });
}
